import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AdminClient,
  PaginatedResponse,
  ApiError,
  fetchAdminClients,
  createAdminClient,
  updateAdminClient,
  deleteAdminClient,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import PhoneField from './PhoneField';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Lock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Users,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';

export default function AdminClients() {
  const [data, setData] = useState<PaginatedResponse<AdminClient> | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formPhone, setFormPhone] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AdminClient | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Monotonic id so a slow response for an old search/page can't overwrite a
  // newer one — only the most recently issued request is allowed to set state.
  const loadReqRef = useRef(0);

  const load = useCallback(async (s: string, p: number) => {
    const reqId = ++loadReqRef.current;
    setLoading(true);
    try {
      const res = await fetchAdminClients(s || undefined, p);
      if (reqId === loadReqRef.current) {
        setData(res);
        setLoadError(false);
      }
    } catch {
      // Only the newest request may report — a stale failure shouldn't flag an
      // error over results that loaded fine afterwards.
      if (reqId === loadReqRef.current) setLoadError(true);
    } finally {
      if (reqId === loadReqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(search, page);
  }, [search, page, load]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / 20)) : 1;

  const openAdd = () => {
    setEditingId(null);
    setFormName('');
    setFormPhone(undefined);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (customer: AdminClient) => {
    setEditingId(customer.id);
    setFormName(customer.name);
    setFormPhone(customer.phone || undefined);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setSaving(true);
    setFormError('');
    try {
      if (editingId) {
        await updateAdminClient(editingId, { name: formName.trim(), phone: formPhone });
      } else {
        await createAdminClient({ name: formName.trim(), phone: formPhone });
      }
      closeForm();
      await load(search, page);
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Something went wrong.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAdminClient(deleteTarget.id);
      setDeleteTarget(null);
      // If that was the only row on a page past the first, step back a page —
      // reloading the same page would request a now out-of-range page (DRF 404s
      // it), which load() swallows, leaving the deleted row on screen.
      if (data && data.results.length === 1 && page > 1) {
        setPage((p) => p - 1); // triggers a reload via the load effect
      } else {
        await load(search, page);
      }
    } catch (err) {
      // Keep the dialog open and show why — a silent failure looks like nothing
      // happened, and the row is still there to prove it didn't.
      setDeleteError(err instanceof ApiError ? err.message : 'Could not delete this client.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
        {/* Header */}
        <div className="border-subtle flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div className="min-w-0">
            <h2 className="text-fg text-base font-bold sm:text-lg">Clients</h2>
            <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
              All customers who have booked at your salon.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="bg-brand-600 hover:bg-brand-700 flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-white transition-colors sm:gap-2 sm:px-4 sm:text-sm"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Client</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* Search */}
        <div className="border-subtle bg-canvas/40 border-b px-4 py-3 sm:px-6">
          <div className="relative">
            <Search className="text-fg-subtle absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name or phone..."
              className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border py-2 pr-4 pl-10 text-sm outline-none focus:border-transparent focus:ring-2"
            />
            {searchInput && (
              <button
                onClick={() => handleSearchChange('')}
                className="text-fg-subtle hover:text-fg-secondary absolute top-1/2 right-3 -translate-y-1/2"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="border-subtle bg-canvas/40 space-y-4 border-b px-4 py-5 sm:px-6"
          >
            {formError && (
              <div className="border-danger/25 bg-danger-soft text-danger rounded-lg border px-3 py-2 text-sm">
                {formError}
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="focus:ring-brand-600 border-default bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2"
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase">
                  Phone <span className="text-fg-subtle font-normal normal-case">(optional)</span>
                </label>
                <PhoneField
                  value={formPhone}
                  onChange={setFormPhone}
                  selectClassName="px-3 py-2 border border-default rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none transition-all bg-surface"
                  inputClassName="flex-1 px-3 py-2 border border-default rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none transition-all bg-surface text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="border-default text-fg-secondary hover:bg-canvas rounded-lg border px-4 py-2 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingId ? 'Save Changes' : 'Add Client'}
              </button>
            </div>
          </form>
        )}

        {/* Refresh failed but we still have prior results on screen */}
        {loadError && data && (
          <div className="border-danger/20 bg-danger-soft text-danger flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm sm:px-6">
            <span className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              Couldn't refresh — showing the last loaded list.
            </span>
            <button
              onClick={() => load(search, page)}
              className="text-danger shrink-0 font-semibold underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-subtle text-fg-muted border-b text-xs font-semibold tracking-wide uppercase">
                <th className="px-4 py-3 sm:px-6">Name</th>
                <th className="hidden px-4 py-3 sm:table-cell sm:px-6">Phone</th>
                <th className="hidden px-4 py-3 sm:table-cell sm:px-6">Type</th>
                <th className="px-4 py-3 text-right sm:px-6">Bookings</th>
                <th className="hidden px-4 py-3 sm:table-cell sm:px-6">Last Visit</th>
                <th className="px-4 py-3 text-right sm:px-6">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-subtle divide-y">
              {loading && !data ? (
                <tr>
                  <td colSpan={6} className="text-fg-subtle px-6 py-12 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : loadError && !data ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <AlertCircle className="text-danger mx-auto h-8 w-8" />
                    <p className="text-fg-muted mt-2 text-sm">Couldn't load clients.</p>
                    <button
                      onClick={() => load(search, page)}
                      className="text-accent hover:text-accent mt-2 text-sm font-semibold"
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : data && data.results.length > 0 ? (
                data.results.map((customer) => (
                  <tr key={customer.id} className="hover:bg-canvas/50 transition-colors">
                    <td className="px-4 py-3 sm:px-6">
                      <div className="text-fg font-medium">{customer.name}</div>
                      <div className="text-fg-muted text-xs sm:hidden">{customer.phone || '—'}</div>
                    </td>
                    <td className="text-fg-secondary hidden px-4 py-3 sm:table-cell sm:px-6">
                      {customer.phone || <span className="text-fg-faint">—</span>}
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell sm:px-6">
                      {customer.isSocial ? (
                        <span className="bg-accent-soft text-accent inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
                          <Lock className="h-3 w-3" />
                          Account
                        </span>
                      ) : (
                        <span className="bg-muted text-fg-secondary rounded-full px-2.5 py-0.5 text-xs font-medium">
                          Walk-in
                        </span>
                      )}
                    </td>
                    <td className="text-fg-secondary px-4 py-3 text-right text-sm tabular-nums sm:px-6">
                      {customer.appointmentCount}
                    </td>
                    <td className="text-fg-muted hidden px-4 py-3 sm:table-cell sm:px-6">
                      {customer.lastAppointment ? (
                        format(new Date(customer.lastAppointment), 'MMM d, yyyy')
                      ) : (
                        <span className="text-fg-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right sm:px-6">
                      {customer.isSocial ? (
                        <Lock
                          className="text-fg-faint ml-auto h-4 w-4"
                          title="Linked to a Calendyz account — read only"
                        />
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(customer)}
                            className="text-fg-subtle hover:bg-muted hover:text-fg-secondary rounded-md p-1.5 transition-colors"
                            title="Edit client"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteError('');
                              setDeleteTarget(customer);
                            }}
                            className="text-fg-subtle hover:bg-danger-soft hover:text-danger rounded-md p-1.5 transition-colors"
                            title="Delete client"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Users className="text-fg-faint mx-auto h-8 w-8" />
                    <p className="text-fg-muted mt-2 text-sm">
                      {search ? 'No clients match your search.' : 'No clients yet.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.count > 20 && (
          <div className="border-subtle flex items-center justify-between border-t px-4 py-3 sm:px-6">
            <p className="text-fg-muted text-xs">
              {data.count} total &middot; Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="border-default text-fg-muted hover:bg-canvas rounded-lg border p-2 transition-colors disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="border-default text-fg-muted hover:bg-canvas rounded-lg border p-2 transition-colors disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Client"
        message={`Are you sure you want to delete ${deleteTarget?.name}? This will remove their record from your salon.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        variant="danger"
        busy={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
